import React, { useRef, useEffect, useMemo, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { useSelector } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

import OrderSummaryInfo from "../../../../components/sections/front/OrderSummaryInfo";
import Form from "../../../../components/form/Form";
import StripePayment from "../../../../components/sections/front/StripePayment";
import DynamicForm from "../../../../components/sections/front/checkout/DynamicForm";
import useForm from "../../../../hooks/useForm";
import { lngDetect } from "../../../../helpers/lngDetect";
import { orderPlace } from "../../../../services/orderService";
import { apiRequest } from "../../../../util/apiUtils";
import PaymentButtons from "../../../../components/sections/front/PaymentButtons";
import { accountCheck } from "../../../../services/eventService";
import Badge from "../../../../components/elements/Badge";
import { parseNestedFields } from "../../../../helpers";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination } from "swiper/modules";
import { useTranslation } from "react-i18next";
import { ticketList } from "../../../../services/ticketService";

const Checkout = () => {
  const { t } = useTranslation();
  const [validationRules, setValidationRules] = useState({});
  const navigate = useNavigate();
  const { eventId } = useParams();
  const location = useLocation();
  const { lang } = useSelector((state) => state?.langProvider);
  const { session } = useSelector((state) => state?.authProvider);
  const {
    event: eventDetailsData,
    eventLoading,
    selectedLanguage,
    eventOrganizer,
    eventPromoter,
  } = useOutletContext();
  const { data } = useSelector((state) => state?.profile);
  const [paymentData, setPaymentData] = useState({
    paypalData: null,
    stripeData: null,
  });
  const hasStripeSecret = Boolean(paymentData?.stripeData?.clientSecret);

  const {
    values,
    handleChange,
    handleSubmit,
    setFieldsValue,
    errors,
    formValidate,
    setErrors,
  } = useForm({
    initialState: {},
    validationRules,
    showToast: true,
  });

  const ticketsIds = (location?.state?.checkoutData || [])?.map(
    (item) => item?._id
  );
  const {
    data: tickets,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["ticket-checkout", ticketsIds],
    enabled: ticketsIds?.length > 0,
    queryFn: async () => {
      const res = await ticketList({ ticket: ticketsIds });
      const data = res?.docs?.data?.map((item) => {
        const find_checkoutItem = location?.state?.checkoutData?.find(
          (c) => c?._id === item?._id
        );
        const find_discount =
          eventPromoter?.data?.data?.info?.settingsPerTicket?.find(
            (elm) =>
              elm?.ticket?.id === item?._id || elm?.ticket?._id === item?._id
          );

        const discount_type = find_discount?.discount?.type;
        const discount_value = Number(find_discount?.discount?.value) || 0;

        let finalPrice = item?.price;

        if (discount_type && discount_value) {
          if (discount_type === "percentage") {
            finalPrice = item?.price - item?.price * discount_value; // e.g. 0.1 = 10%
          } else if (discount_type === "fixed") {
            finalPrice = item?.price - discount_value;
          }

          // prevent negative price
          if (finalPrice < 0 || !finalPrice) finalPrice = 0;
        }

        return {
          ...item,
          id: item?._id,
          title: lngDetect(item?.name),
          originalPrice: item?.price,
          buy_quentity: find_checkoutItem?.buy_quentity,
          info: find_checkoutItem?.info,
          price: finalPrice,
        };
      });

      return { ...res?.docs, data };
    },
  });

  // checkoutData =============>
  const checkoutData = useMemo(() => {
    // please check previous logic on frontLayout.js "promoter-details" query key
    if (!location?.state?.promoterUid) {
      return (tickets?.data || [])?.map((item) => {
        return {
          ...item,
          price: item?.originalPrice,
          totalPrice: item?.buy_quentity * item?.originalPrice,
        };
      });
    } else {
      return tickets?.data || [];
    }
  }, [location?.state, tickets?.data]);

  const {
    data: accountData,
    isLoading: isAccountLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["account-status-checkout", eventOrganizer?.data?.userData?._id],
    queryFn: async () => {
      const res = await accountCheck({
        user: eventOrganizer?.data?.userData?._id,
      });
      return res?.data;
    },
    enabled: !!eventOrganizer?.data?.userData?._id,
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || "Failed to check Stripe account status"
      );
    },
  });

  const {
    mutate: checkoutHandler,
    data: orderCheckoutData,
    isPending: checkOutInPending,
  } = useMutation({
    mutationFn: async (val) => {
      if (
        eventOrganizer?.data?.setting?.bannedEmail?.some((email) =>
          email.includes(data?.email)
        )
      ) {
        navigate(`/${lang}/accessDenied`, {
          state: {
            eventId,
            message: lngDetect(
              eventOrganizer?.data?.setting?.bannedEmailMessage
            ),
            route: `/${lang}/order-summary/${eventId}`,
          },
        });
        return;
      }
      const value = parseNestedFields(val);

      const items = checkoutData
        ?.map((item, i) =>
          value?.attendeeData?.[i]?.map((attendee, j) => ({
            ticket: item?._id,
            quantity: 1,
            attendeeData: attendee
              ? Object.keys(attendee)?.map((key) => ({
                  question: key,
                  answers: attendee?.[key],
                  ...(["First Name", "Last Name", "Email"].includes(key) && {
                    isEdited: true,
                  }),
                }))
              : undefined,
            // attendeeInfo: {
            //   firstName: attendee?.["First Name"] || "",
            //   lastName: attendee?.["Last Name"] || "",
            //   email: attendee?.Email || "",
            // },
          }))
        )
        ?.filter(Boolean)
        ?.flat(Infinity);

      const order_payload = {
        event: eventDetailsData?.data?._id,
        organization: eventDetailsData?.data?.organizationData?._id,
        billingInfo: value?.billingInfo
          ? Object.keys(value?.billingInfo)?.map((key) => ({
              question: key,
              answers: value?.billingInfo?.[key],
            }))
          : undefined,
        items,
        promoterUid: location?.state?.promoterUid,
        ...(location?.state?.placedBy && {
          placedBy: location?.state?.placedBy,
        }),
        payment: { gateway: value?.type },
      };

      const orderRes = await orderPlace(order_payload);

      if (orderRes?.status !== 201) {
        throw new Error(orderRes?.message || "Order creation failed");
      }

      const paymentRes = await apiRequest(
        "post",
        "payment/plan-create-with-payment-intent",
        {
          body: { orderUid: orderRes?.data?.uid },
        }
      );

      if (paymentRes?.status !== 200) {
        throw new Error(
          paymentRes?.data?.data?.message ||
            paymentRes?.data?.data?.error ||
            "Payment setup failed"
        );
      }

      if (paymentRes?.data?.data?.data?.approvalUrl) {
        window.open(paymentRes?.data?.data?.data?.approvalUrl, "_blank");
      }
      if (paymentRes?.data?.data?.data?.clientSecret) {
        setPaymentData({
          paypalData: null,
          stripeData: paymentRes?.data?.data?.data,
        });

        return {
          res: orderRes?.data,
          ...(paymentRes?.data?.data?.data?.approvalUrl
            ? { paypal_res: paymentRes?.data?.data?.data }
            : {
                stripe_res: paymentRes?.data,
              }),
        };
      }
    },
    onSuccess: (res) => {
      if (res?.status === 201) {
        toast.success(res?.message);
      }
    },
    onError: (error) => {
      toast.error(error?.message || "Something went wrong during checkout");
    },
  });

  useEffect(() => {
    if (eventDetailsData?.data?._id && checkoutData?.length > 0) {
      checkoutData?.map((item, i) =>
        Array.from({ length: item?.buy_quentity }, (_, j) =>
          setFieldsValue({
            [`attendeeData[${i}][${j}][First Name]`]: session?.user?.firstName,
            [`attendeeData[${i}][${j}][Last Name]`]: session?.user?.lastName,
            [`attendeeData[${i}][${j}][Email]`]: session?.user?.email,
          })
        )
      );
    }
  }, [eventDetailsData?.data?._id, checkoutData?.length, session?.user?._id]);

  const pagination = {
    clickable: true,
    renderBullet: function (index, className) {
      return `<span class="${className} w-8 h-8 rounded-lg"><span class="m-auto flex w-full h-full justify-center text-white items-center text-sm">${
        index + 1
      }</span></span>`;
    },
  };

  return (
    <section className="relative pt-10 md:pt-14 pb-24 bg-magnolia">
      <div className="max-w-6xl xl:max-w-7xl 2xl:max-w-8xl 3xl:max-w-9xl w-full mx-auto relative px-5 xl:px-0">
        <h3 className="text-2xl md:text-3xl text-deep-purple uppercase font-bold mb-2">
          {t("Billing information")}
        </h3>
        <div className="text-base text-slate-600 mb-5">
          Logged in as {session?.user?.email}
        </div>
        <Form onSubmit={checkoutHandler} handleSubmit={handleSubmit}>
          <div className="grid grid-cols-12 gap-4 items-start">
            <div className="col-span-12 md:col-span-6 lg:col-span-7 space-y-4">
              <div className="bg-white p-6 xl:p-10 rounded-3xl space-y-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-lg">
                    {t("Billing information")}
                  </h3>
                </div>
                <DynamicForm
                  sectionFor={"billing"}
                  validationStartFrom={`billingInfo`}
                  errors={errors}
                  formValidate={formValidate}
                  values={values}
                  setFieldsValue={setFieldsValue}
                  handleChange={handleChange}
                  validationRules={validationRules}
                  setValidationRules={(val) => {
                    if (val === "reset") {
                      setValidationRules({});
                      setErrors({});
                    } else {
                      setValidationRules((pre) => ({ ...pre, ...val }));
                    }
                  }}
                />
              </div>
              {checkoutData?.map((ck_data, i) => (
                <Swiper
                  pagination={pagination}
                  modules={[Pagination]}
                  className="mySwiper"
                  key={i?.toString()}
                >
                  {Array.from({ length: ck_data?.buy_quentity }, (_, j) => (
                    <SwiperSlide key={`${ck_data?._id}-${i}-${j}`}>
                      <div className="bg-white p-6 xl:p-10 rounded-3xl space-y-5">
                        <div className="flex items-center justify-between">
                          <h3 className="font-medium text-lg">
                            {j + 1} : {lngDetect(ck_data?.name)}
                          </h3>
                          <Badge
                            className={"text-sm"}
                            type={"success"}
                            label={t(
                              ck_data?.type === "ticket" ? "Ticket" : "Addon"
                            )}
                          />
                        </div>
                        <DynamicForm
                          sectionFor={"attendee"}
                          validationStartFrom={`attendeeData[${i}][${j}]`}
                          errors={errors}
                          formValidate={formValidate}
                          values={values}
                          handleChange={handleChange}
                          setFieldsValue={setFieldsValue}
                          validationRules={validationRules}
                          setValidationRules={(val) => {
                            if (val === "reset") {
                              setValidationRules({});
                              setErrors({});
                            } else {
                              setValidationRules((pre) => ({ ...pre, ...val }));
                            }
                          }}
                          category={ck_data?.type}
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              ))}
            </div>
            <div className="col-span-12 md:col-span-6 lg:col-span-5 space-y-4 md:sticky md:top-32">
              <OrderSummaryInfo checkoutData={checkoutData} />
              {!hasStripeSecret &&
                !paymentData?.paypalData?.approvalUrl &&
                (tickets?.data || [])?.length > 0 && (
                  <>
                    <div className="text-sm font-medium">
                      {t("By selecting Place Order, I agree to the")}{" "}
                      <Link
                        className="text-deep-purple font-semibold"
                        to={window.location.origin}
                      >
                        {t("Terms of Service")}
                      </Link>
                    </div>
                    <PaymentButtons
                      onPaymentHandler={({ type }) => {
                        // checkoutHandler({ ...values, type });
                        setFieldsValue({ type: type });
                      }}
                      loading={checkOutInPending}
                      {...accountData?.data}
                    />
                  </>
                )}
              {hasStripeSecret && (
                <StripePayment
                  clientSecret={paymentData?.stripeData?.clientSecret}
                  intentId={paymentData?.stripeData?.intentId}
                  merchantId={paymentData?.stripeData?.merchantId}
                />
              )}
            </div>
          </div>
        </Form>
      </div>
    </section>
  );
};

export default Checkout;
